<template>
  <div>
    <!-- Content Grid -->
    <Dialog
      v-model="isWindowOpen"
      :show-icon="false"
      :persistent="true"
      max-width="800px"
      content-class="patient-picker-dialog"
    >
      <template #content>
        <!-- Title -->
        <div class="dialog-title">
          {{ t("Select from my patients") }}
        </div>

        <!-- Filter & New Patient Button -->
        <div class="dialog-filter-and-new-patient">
          <ToolbarSearchInput
            v-model="searchInput"
            :placeholder="t('Find a patient')"
            class="toolbar-search-input"
          />
          <Button
            @click="openCreationWindow"
          >
            {{ t("New Patient") }}
          </Button>
        </div>

        <!-- Patient List -->
        <v-data-table
          v-model:page="page"
          :headers="headers"
          :items="filteredPatients"
          :items-per-page="itemsPerPage"
          :items-length="filteredPatients.length"
          :loading="isLoading"
          :loading-text="t('Loading Content')"
          :no-data-text="t('No data available')"
          multi-sort
          hide-default-footer
        >
          <template #item.firstName="{ item }">
            {{ item.firstName }} {{ item.lastName }}
          </template>

          <template #item.dateOfBirth="{ item }">
            {{ formatDisplayDate(item.dateOfBirth) }}
          </template>

          <template #item.sexId="{ item }">
            {{ t(convertFrontendSexValueToFormBuilderValue(item.sexId)) }}
          </template>
          <template #item.actions="{ item }">
            <Button
              color="text-primary"
              icon-btn
              icon-with-background
              class="mr-2 btn-fab--small"
              @click="selectItem(item)"
            >
              <v-icon
                class="icon-flipped"
                size="20"
              >
                mdi-keyboard-backspace
              </v-icon>
            </Button>
          </template>
        </v-data-table>

        <Pagination
          v-model="page"
          :page-count="pageCount"
        />
      </template>
    </Dialog>

    <!-- Patient Creation -->
    <PatientCreationWindow
      ref="creationWindow"
      @selectNewPatient="selectNewPatient"
    />

    <!-- Show Buttons -->
    <div class="patient-picker-link text-end mb-1">
      <div @click="openPickerWindow">
        {{ t("Select from my patients") }}
        <v-icon
          end
          class="icon-flipped"
          color="primary"
          size="22"
        >
          mdi-keyboard-backspace
        </v-icon>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { usePatientsStore } from "@/stores/patients";
import { usePhysiciansStore } from "@/stores/physicians";
import Button from "@/components/input-components/Button.vue";
import Pagination from "@/components/common/Pagination.vue";
import Dialog from "@/components/dialogs/Dialog.vue";
import PatientCreationWindow from "@/components/patients/PatientCreationWindow.vue";
import ToolbarSearchInput from "@/components/common/ToolbarSearchInput.vue";
import { FORM_FLAGS } from "@/utils/constants";
import {formatDisplayDate, useDateHelper} from '@/composables/useDateHelper'
import { Patient } from "@/entities/patient";
import { useI18n } from 'vue-i18n';
import {convertFrontendSexValueToFormBuilderValue} from "@/utils";

const { t } = useI18n();
const { sortDate } = useDateHelper();

const props = defineProps<{ flag: string }>();

const patientsStore = usePatientsStore();
const physiciansStore = usePhysiciansStore();

const isWindowOpen = ref(false);
const page = ref(1);
const itemsPerPage = ref(20);
const isLoading = ref(false);

const searchInput = computed({
  get: () => patientsStore.searchInput,
  set: (value: string) => patientsStore.searchInput = value,
});

const headers = [
  {
    title: t('Patient name'),
    align: "start",
    sortable: true,
    value: "firstName",
  },
  {
    title: t('CeGaT ID'),
    value: "patientNumber",
    sortable: true,
  },
  {
    title: t("Patient d.o.b"),
    value: "dateOfBirth",
    sort: (a: string, b: string) => sortDate(a, b),
  },
  {
    title: t('Sex'),
    value: "sexId",
    sortable: true,
  },
  { title: "Actions", value: "actions" },
];

const emit = defineEmits(["patientSelected", "parentSelected"]);

const patients = computed(() => patientsStore.patients);
const patient = computed(() => patientsStore.patient);
const getSexById = patientsStore.getSexById;
const physicianId = computed(() => physiciansStore.physician?.userId || "");

const filteredPatients = computed(() => {
  const q = (searchInput.value ?? '').trim().toLowerCase();
  if (!q) return patients.value;

  return patients.value.filter(p => {
    const first = (p.firstName ?? '').toLowerCase();
    const last = (p.lastName ?? '').toLowerCase();
    const number = (p.patientNumber ?? '').toLowerCase();
    return first.includes(q) || last.includes(q) || number.includes(q);
  });
});

const pageCount = computed(() => {
  const total = filteredPatients.value.length;
  return Math.max(1, Math.ceil(total / itemsPerPage.value));
});

function openPickerWindow() {
  isWindowOpen.value = true;
}

function closePickerWindow() {
  isWindowOpen.value = false;
}

const creationWindow = ref<InstanceType<typeof PatientCreationWindow> | null>(null);

function openCreationWindow() {
  creationWindow.value?.openCreationWindow();
}

function selectItem(item: Patient) {
  if (props.flag === FORM_FLAGS.isPatientInfo) {
    patientsStore.patient = item;
    emit("patientSelected", item);
  } else if (props.flag === FORM_FLAGS.isParentConsent) {
    emit("parentSelected", item);
  }
  closePickerWindow();
}

function selectNewPatient() {
  if (props.flag === FORM_FLAGS.isPatientInfo) {
    emit("patientSelected", patient.value);
  } else if (props.flag === FORM_FLAGS.isParentConsent) {
    emit("parentSelected", patient.value);
  }
  closePickerWindow();
}

async function fetchSexes() {
  isLoading.value = true;
  await patientsStore.fetchPatientsSexes();
  await fetchPatients();
  isLoading.value = false;
}

async function fetchPatients() {
  if (physicianId.value) {
    await patientsStore.fetchPatientsByPhysicianId(physicianId.value);
  } else {
    await patientsStore.fetchPatients(true);
  }
}

function refreshPatients(event: StorageEvent) {
  if (event.key === "refreshPatients") {
    fetchPatients();
    window.localStorage.removeItem("refreshPatients");
  }
}

watch(searchInput, () => {
  page.value = 1;
});

onMounted(() => {
  fetchSexes();
  window.addEventListener("storage", refreshPatients);
});

onBeforeUnmount(() => {
  window.removeEventListener("storage", refreshPatients);
});
</script>

<style lang="scss">
.patient-picker-link {
  color: rgb(var(--v-theme-primary));

  :hover {
    cursor: pointer;
  }
}
.patient-picker-dialog {
  .dialog-title {
    text-transform: uppercase;
    font-weight: bold;
    margin-bottom: 30px;
    font-size: 20px;
    letter-spacing: 1px;
    margin-top: -20px;
  }
  .dialog-filter-and-new-patient {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .v-card {
    padding: 25px 35px;
  }
  .v-card__actions {
    justify-content: center;
  }

  @media #{map.get($display-breakpoints, 'xs-only')} {
    .toolbar-search-input {
      flex-direction: column;

      > button {
        margin-top: 0.5rem;
        margin-left: 0 !important;
      }
    }
  }
}
</style>
